import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import * as Styles from './Summary.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import bannerCriticalPath from '../../media/banner-critical.svg';
import bannerSuccessPath from '../../media/banner-success.svg';
import bannerWarningPath from '../../media/banner-warning.svg';
import bannerGradientPath from '../../media/banner-gradient.svg';
import ProtectionCard, { PROTECTION_CARD_TYPES } from '../ProtectionCard';

import {
  AlertV2,
  IconCodeBold,
  IconFolderOutline,
  Skeleton,
  Toggle,
  Tooltip,
} from 'gatorcomponents';

const SummaryContext = createContext({ content: {}, loading: false });
const useSummaryContext = () => useContext(SummaryContext);

const BANNER_CONFIG = {
  trialActive: {
    icon: bannerSuccessPath,
    bannerType: 'success',
    alt: 'Trial Active',
    testId: 'trial-active-message',
  },
  cleaningInProgress: {
    icon: bannerWarningPath,
    bannerType: 'warning',
    alt: 'Cleaning in Progress',
    testId: 'cleaning-in-progress-message',
  },
  limitedProtection: {
    icon: bannerWarningPath,
    bannerType: 'warning',
    alt: 'Limited Protection',
    testId: 'limited-protection-message',
  },
  fullProtectionActive: {
    icon: bannerGradientPath,
    bannerType: 'success-gradient',
    alt: 'Full Protection Active',
    testId: 'full-protection-active-message',
  },
  expiredProtection: {
    icon: bannerCriticalPath,
    bannerType: 'critical',
    alt: 'Expired Protection',
    testId: 'expired-protection-message',
  },
};

const Banner = ({ type, trialDaysLeft }) => {
  const { content } = useSummaryContext();
  const config = BANNER_CONFIG[type];
  if (!config) return null;

  const contentMap = {
    trialActive: content.alerts.trialActive(trialDaysLeft || 0),
    cleaningInProgress: content.alerts.cleaningInProgress,
    limitedProtection: content.alerts.limitedProtection,
    fullProtectionActive: content.alerts.fullProtectionActive,
    expiredProtection: content.alerts.expiredProtection,
  };

  const isFullProtection = type === 'fullProtectionActive';

  return (
    <Styles.StatusBanner type={config.bannerType} data-testid={config.testId}>
      <Styles.StatusIcon src={config.icon} alt={config.alt} />
      {isFullProtection ? (
        <Styles.FullProtectionBannerContent>
          <span dangerouslySetInnerHTML={{ __html: contentMap[type] }} />
        </Styles.FullProtectionBannerContent>
      ) : (
        <span dangerouslySetInnerHTML={{ __html: contentMap[type] }} />
      )}
    </Styles.StatusBanner>
  );
};

Banner.propTypes = {
  type: PropTypes.oneOf(Object.keys(BANNER_CONFIG)).isRequired,
  trialDaysLeft: PropTypes.number,
};

const Title = ({
  loading = false,
  enabled,
  onToggle,
}) => {
  const { content } = useSummaryContext();
  const showToggle = enabled !== undefined && typeof onToggle === 'function';

  if (loading) {
    return (
      <Styles.TitleSkeletonWrapper>
        <Skeleton height="100%" width="240px" />
      </Styles.TitleSkeletonWrapper>
    );
  }

  if (showToggle) {
    return (
      <Styles.TitleWithToggleRow>
        <Styles.TitleLabel dangerouslySetInnerHTML={{ __html: content.alerts.automaticRemovalLabel(enabled) }} />
        <Styles.AutomaticRemovalRow>
          <Tooltip
            content={content.alerts.automaticRemovalTooltip}
            preferredPosition="topLeft"
          >
            <Styles.ToggleLabel onClick={onToggle}>
              <Toggle
                testId="automatic-cleaning-toggle"
                checked={enabled}
                controlledByFather
              />
            </Styles.ToggleLabel>
          </Tooltip>
        </Styles.AutomaticRemovalRow>
      </Styles.TitleWithToggleRow>
    );
  }

  return <Styles.Title>{content.title}</Styles.Title>;
};

Title.propTypes = {
  loading: PropTypes.bool,
  enabled: PropTypes.bool,
  onToggle: PropTypes.func,
};

Title.defaultProps = {
  loading: false,
  enabled: undefined,
  onToggle: undefined,
};

const Metrics = ({ maliciousCodesRemoved, compromisedFiles }) => {
  const { content, loading } = useSummaryContext();
  return (
    <Styles.Content>
      <Styles.Column>
        <Styles.ColumnIcon>
          <IconCodeBold />
        </Styles.ColumnIcon>
        <Styles.ColumnTextWrapper>
          <Styles.ColumnTitle>{content.maliciousCodesRemoved}</Styles.ColumnTitle>
          <Styles.ColumnDescription>{content.maliciousCodesRemovedDescription}</Styles.ColumnDescription>
          <Styles.ColumnValue>
            {loading ? <Skeleton height="40px" width="80px" /> : maliciousCodesRemoved}
          </Styles.ColumnValue>
        </Styles.ColumnTextWrapper>
      </Styles.Column>
      <Styles.Column>
        <Styles.Divider />
      </Styles.Column>
      <Styles.Column>
        <Styles.ColumnIcon>
          <IconFolderOutline />
        </Styles.ColumnIcon>
        <Styles.ColumnTextWrapper>
          <Styles.ColumnTitle>{content.compromisedFiles}</Styles.ColumnTitle>
          <Styles.ColumnDescription>{content.compromisedFilesDescription}</Styles.ColumnDescription>
          <Styles.ColumnValue>
            {loading ? <Skeleton height="40px" width="80px" /> : compromisedFiles}
          </Styles.ColumnValue>
        </Styles.ColumnTextWrapper>
      </Styles.Column>
    </Styles.Content>
  );
};

Metrics.propTypes = {
  maliciousCodesRemoved: PropTypes.number,
  compromisedFiles: PropTypes.number,
};

Metrics.defaultProps = {
  maliciousCodesRemoved: 0,
  compromisedFiles: 0,
};

const SummaryProtectionCard = ({
  type,
  onActivateProtectionPro,
  onActivateTrial,
  isAAClient,
  hideButtons,
  hideActivateTrial,
  isTurbo,
}) => (
  <ProtectionCard
    type={type}
    isAAClient={isAAClient}
    hideButtons={hideButtons}
    hideActivateTrial={hideActivateTrial}
    onActivateProtectionPro={onActivateProtectionPro}
    onActivateTrial={onActivateTrial}
    isTurbo={isTurbo}
  />
);

SummaryProtectionCard.propTypes = {
  type: PropTypes.oneOf(Object.values(PROTECTION_CARD_TYPES)).isRequired,
  onActivateProtectionPro: PropTypes.func,
  onActivateTrial: PropTypes.func,
  isAAClient: PropTypes.bool,
  hideButtons: PropTypes.bool,
  hideActivateTrial: PropTypes.bool,
};

SummaryProtectionCard.defaultProps = {
  onActivateProtectionPro: undefined,
  onActivateTrial: undefined,
  isAAClient: false,
  hideButtons: false,
  hideActivateTrial: false,
};

const ProtectionCardSkeleton = () => (
  <Skeleton width="534px" height="205px" />
);

const TeamAlert = ({ variant, threatsNumber, onOpenTicket }) => {
  const { content } = useSummaryContext();

  if (variant === 'intervention') {
    return (
      <AlertV2
        title={content.alerts.teamIntervention.title(threatsNumber)}
        description={content.alerts.teamIntervention.description}
        primaryButtonLabel={content.alerts.teamIntervention.buttonLabel}
        onPrimaryAction={onOpenTicket}
        variant="critical"
      />
    );
  }

  return (
    <AlertV2
      title={content.alerts.teamInterventionResolved.title}
      description={content.alerts.teamInterventionResolved.description}
      primaryButtonLabel={content.alerts.teamInterventionResolved.buttonLabel}
      onPrimaryAction={onOpenTicket}
      variant="success"
    />
  );
};

TeamAlert.propTypes = {
  variant: PropTypes.oneOf(['intervention', 'resolved']).isRequired,
  threatsNumber: PropTypes.number,
  onOpenTicket: PropTypes.func,
};

const Banners = ({ children }) => <>{children}</>;
const Main = ({ children, testId }) => (
  <Styles.Container data-testid={testId}>
    <Styles.Adapter>
      <Styles.TitleAndColumnsContainer>
        {children}
      </Styles.TitleAndColumnsContainer>
    </Styles.Adapter>
  </Styles.Container>
);
const Alerts = ({ children }) => <Styles.AlertWrapper>{children}</Styles.AlertWrapper>;

const Summary = ({
  content,
  loading,
  testId,
  children,
}) => (
  <SummaryContext.Provider value={{ content, loading }}>
    {children({
      Banners,
      Main: function MainWithTestId(props) {
        return <Main testId={testId} {...props} />;
      },
      Alerts,
    })}
  </SummaryContext.Provider>
);

Summary.propTypes = {
  content: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  testId: PropTypes.string,
  children: PropTypes.func.isRequired,
};

Summary.defaultProps = {
  loading: false,
  testId: 'summary',
};

const SummaryWithErrorBoundary = withErrorBoundary(Summary, 'Summary');

SummaryWithErrorBoundary.Banner = Banner;
SummaryWithErrorBoundary.Title = Title;
SummaryWithErrorBoundary.Metrics = Metrics;
SummaryWithErrorBoundary.ProtectionCard = SummaryProtectionCard;
SummaryWithErrorBoundary.ProtectionCardSkeleton = ProtectionCardSkeleton;
SummaryWithErrorBoundary.TeamAlert = TeamAlert;
SummaryWithErrorBoundary.PROTECTION_CARD_TYPES = PROTECTION_CARD_TYPES;

export { PROTECTION_CARD_TYPES };
export default SummaryWithErrorBoundary;
